/**
 * Auth Client Utilities
 * Client-side authentication helpers
 */

export interface User {
  id: string;
  githubId: number;
  username: string;
  email: string | null;
  avatarUrl: string;
  name: string | null;
  isPro: boolean;
  subscriptionTier: 'free' | 'pro';
  subscriptionStatus: string;
  subscriptionPeriodEnd: string | null;
}

export interface Session {
  user: User | null;
}

let cachedSession: Session | null = null;
let sessionPromise: Promise<Session> | null = null;
let cachedToken: string | null = null;

/**
 * Get current session (cached)
 */
export async function getSession(): Promise<Session> {
  if (cachedSession) return cachedSession;
  if (sessionPromise) return sessionPromise;

  sessionPromise = fetchSession();
  cachedSession = await sessionPromise;
  sessionPromise = null;

  return cachedSession;
}

/**
 * Fetch fresh session from server
 */
async function fetchSession(): Promise<Session> {
  try {
    const res = await fetch('/api/auth/session', {
      credentials: 'include',
    });

    if (!res.ok) {
      return { user: null };
    }

    return await res.json();
  } catch {
    return { user: null };
  }
}

/**
 * Refresh session (clear cache and re-fetch)
 */
export async function refreshSession(): Promise<Session> {
  cachedSession = null;
  return getSession();
}

/**
 * Start sign-in flow (redirect to GitHub)
 */
export function signIn(): void {
  window.location.href = '/api/auth/signin/github';
}

/**
 * Sign out
 */
export async function signOut(): Promise<void> {
  await fetch('/api/auth/signout', {
    method: 'POST',
    credentials: 'include',
  });

  cachedSession = null;
  cachedToken = null;
  window.location.href = '/';
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(session: Session | null): boolean {
  return session?.user !== null;
}

/**
 * Check if user has pro subscription
 */
export function isPro(session: Session | null): boolean {
  return session?.user?.isPro === true;
}

/**
 * Get GitHub access token (fetched securely from server)
 * Token is NOT stored in session for security - fetched separately when needed
 */
export async function getAccessToken(): Promise<string | null> {
  if (cachedToken) return cachedToken;

  try {
    const res = await fetch('/api/auth/token', {
      credentials: 'include',
    });

    if (!res.ok) return null;

    const { accessToken } = await res.json();
    cachedToken = accessToken;
    return accessToken;
  } catch {
    return null;
  }
}

/**
 * Clear cached token (call on signout)
 */
export function clearTokenCache(): void {
  cachedToken = null;
}
